import { useState } from 'react'
import { X, Loader2, Sparkles } from 'lucide-react'
import { useAIHint } from '../hooks/useAIHint'

interface Props {
  initialContext: string
  userRole: string
  onClose: () => void
}

function renderHintText(text: string): React.ReactNode {
  const lines = text.split('\n')
  return lines.map((line, i) => {
    const trimmed = line.trim()
    if (!trimmed) return null
    const isBullet = trimmed.startsWith('- ') || trimmed.startsWith('• ')
    const content = isBullet ? trimmed.slice(2) : trimmed
    const parts = content.split(/\*\*(.*?)\*\*/g)
    const rendered = parts.map((part, j) =>
      j % 2 === 1 ? <strong key={j}>{part}</strong> : part,
    )
    if (isBullet) {
      return (
        <li key={i} className="ml-4 list-disc">
          {rendered}
        </li>
      )
    }
    return <p key={i} className={i > 0 ? 'mt-2' : ''}>{rendered}</p>
  }).filter(Boolean)
}

export function AIHintPanel({ initialContext, userRole, onClose }: Props) {
  const { hint, loading, error, fetchHint } = useAIHint()
  const [freeText, setFreeText] = useState('')
  const [submitted, setSubmitted] = useState(false)

  useState(() => {
    fetchHint(initialContext, userRole)
  })

  const handleAskAnother = () => {
    if (!freeText.trim()) return
    setSubmitted(true)
    fetchHint(freeText.trim(), userRole)
    setFreeText('')
  }

  return (
    <div className="fixed bottom-32 right-6 z-[9500] w-80 rounded-xl border border-gray-700 bg-gray-800 shadow-2xl">
      <div className="flex items-center justify-between border-b border-gray-700 px-4 py-3">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-indigo-400" />
          <span className="text-sm font-semibold text-gray-100">AI Guide</span>
        </div>
        <button
          onClick={onClose}
          className="rounded-md p-1 text-gray-400 hover:bg-gray-700 hover:text-gray-100"
          aria-label="Close AI hint panel"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="p-4">
        {loading && (
          <div className="flex items-center gap-2 text-sm text-gray-400">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Fetching answer...</span>
          </div>
        )}

        {!loading && error && (
          <p className="text-sm text-red-400">{error}</p>
        )}

        {!loading && hint && !error && (
          <div className="text-sm text-gray-300 leading-relaxed">
            {renderHintText(hint)}
          </div>
        )}

        <div className="mt-4 border-t border-gray-700 pt-3">
          <p className="mb-2 text-xs text-gray-500">
            {submitted ? 'Ask another question' : 'Have another question?'}
          </p>
          <div className="flex gap-2">
            <input
              type="text"
              value={freeText}
              onChange={(e) => setFreeText(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAskAnother()}
              placeholder="Ask about this step..."
              className="flex-1 rounded-md border border-gray-600 bg-gray-700 px-3 py-1.5 text-xs text-gray-100 placeholder-gray-500 focus:border-primary-500 focus:outline-none"
            />
            <button
              onClick={handleAskAnother}
              disabled={!freeText.trim() || loading}
              className="rounded-md bg-primary-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-700 disabled:opacity-50"
            >
              Ask
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
